import { tWithKeyAttr as t } from "../../i18nHelper";
import styles from "./sms.module.css";
import React, { ChangeEvent, useEffect, useState } from "react";
import {
  useDeleteSmsMessageMutation,
  useGetAllSmsMessagesMutation,
} from "../../services/api/utils.api";
import { useAppSelector } from "../../services/hooks";
import { smsMessagesSelector } from "../../services/selectors/utils.selectors";
import Loader from "../../components/loader/loader";
import Modal from "../../components/modals/modal/Modal";
import DeleteConfirmation from "../../components/modals/deleteConfirmation/deleteConfirmation";
import { adminSelector } from "../../services/selectors/user.selectors";
import { TSmsMessage } from "../../services/types/utils.types";
import { filterObjectsByString } from "../../utils/search";
import GeneralSuccessPopup from "../../components/modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../../components/modals/errorPopup/errorPopup";
import { useNavigate } from "react-router-dom";
import BackArrowButton from "../../components/backArrowButton/backArrowButton";
import AddOrEditSmsForm from "../../components/forms/addOrEditSms/addOrEditSms";
import { initialSmsMessage } from "../../utils/constants";
import { TLabeledItem } from "../../types/types";
import { SingleValue } from "react-select";
import ListPagination from "../../components/listPagination/listPagination";
import ActionButtonTooltip from "../../components/ActionButtonTooltip/actionButtonTooltip";
import { useActions } from "../../services/hooks/useActions";
import { currentSMSSelector } from "../../services/selectors/users.selectors";
import ExportTableButtons from "../../components/exportTableButtons/ExportTableButtons";

function SmsPage() {
  const navigate = useNavigate();
  const smsMessages = useAppSelector(smsMessagesSelector);
  const admin = useAppSelector(adminSelector);

  const [inputValue, setInputValue] = useState<string>("");
  const [openModal, setOpenModal] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [showAddOrEditSmsForm, setShowAddOrEditSmsForm] = useState(false);
  const [smsToDelete, setSmsToDelete] = useState<TSmsMessage | undefined>(
    undefined
  );
  const [smsToEdit, setSmsToEdit] = useState<TSmsMessage>(initialSmsMessage);
  const [filteredMessages, setFilteredMessages] = useState<TSmsMessage[]>([]);

  //pagination
  const limitNumber = 8;
  const [limits, setLimits] = useState({ start: 1, end: limitNumber });

  const [getAllSmsMessages, { isLoading }] = useGetAllSmsMessagesMutation();
  const [deleteSms] = useDeleteSmsMessageMutation();

  useEffect(() => {
    getAllSmsMessages(admin.companyId);
  }, [getAllSmsMessages, admin.companyId]);

  useEffect(() => {
    setFilteredMessages(smsMessages);
  }, [setFilteredMessages, smsMessages]);

  //add filter logic
  useEffect(() => {
    setFilteredMessages(filterObjectsByString(inputValue, smsMessages));
    setLimits({ start: 1, end: limitNumber });
  }, [setFilteredMessages, smsMessages, inputValue]);

  const onInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleDelete = () => {
    if (!smsToDelete) return;
    deleteSms({ companyId: admin.companyId, body: smsToDelete })
      .then((res) => {
        if ("data" in res && res.data) setOpenModalSuccess(true);
        else if ("error" in res) setOpenModalError(true);
      })
      .catch(() => setOpenModalError(true));
  };

  const { setCurrentSMS } = useActions();
  const selectSMS = (id: number) => () => {
    setCurrentSMS(id);
  };
  const currentSMS = useAppSelector(currentSMSSelector);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setSmsToEdit((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleSelectChange = (newValue: SingleValue<TLabeledItem>) => {
    newValue &&
      setSmsToEdit((prev) => ({
        ...prev,
        statusId: +newValue.value,
      }));
  };

  if (showAddOrEditSmsForm) {
    return (
      <div className={styles.formContainer}>
        <AddOrEditSmsForm
          form={smsToEdit}
          handleChange={handleChange}
          handleSelectChange={handleSelectChange}
          onClose={() => {
            setShowAddOrEditSmsForm(false);
            setSmsToEdit(initialSmsMessage);
          }}
        />
      </div>
    );
  }
  return (
    <section className="questionnaires">
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={() => navigate(-1)}
        position={"right"}
      />
      <div className={`header ${styles.header}`} style={{ gap: '10px' }}>
        <button
          type={"button"}
          className="addBtn"
          onClick={() => {
            setShowAddOrEditSmsForm(true);
          }}
        >
          {t('ADD_SMS')}
          <span className="plus">+</span>
        </button>

        <input
          type={"text"}
          placeholder={t('SEARCH_FOR')}
          className="searchInput"
          onChange={onInputChange}
          value={inputValue}
        />
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-start', width: '97%', marginTop: '10px', marginBottom: '10px' }}>
        <ExportTableButtons
          entity="SMSMessagesList"
          filters={{ CompanyId: admin.companyId }}
        />
      </div>
      {isLoading ? (
        <Loader />
      ) : filteredMessages.length > 0 ? (
        <article className="section">
          <h3 className="sectionTitle">{t('SMS_LIST')}</h3>
          <ul className={`list ${styles.list}`}>
            <li className={`listRow listRow_header ${styles.listRow}`}>
              <p className="listCell">{t('NUMBER_1')}</p>
              <p className="listCell">{t('NAME_2')}</p>
              <p className="listCell">{t('MESSAGE_NOTIFICATION')}</p>
              <p className="listCell">{t("COLUMN_STATUS")}</p>
              <p className="listCell">{t("COLUMN_OPTIONS")}</p>
            </li>
            {filteredMessages.map(
              (m, index) =>
                index >= limits.start - 1 &&
                index < limits.end && (
                  <li
                    key={m.id}
                    className={`listRow ${styles.listRow} ${
                      index % 2 === 0 && "listRow_colored"
                    }
                    ${currentSMS === m.id ? "listRow_selected" : ""}`}
                    onClick={selectSMS(m.id)}
                  >
                    <p className={`listCell ${styles.listCell}`}>{m.id}</p>
                    <p className={`listCell ${styles.listCell}`}>{m.title}</p>
                    <p className={`listCell ${styles.listCell}`}>
                      {m.body?.split("\n").map((line, index) => (
                        <React.Fragment key={index}>
                          {line}
                          <br />
                        </React.Fragment>
                      )) || ""}
                    </p>
                    <p className={`listCell`}>
                      {m.statusId === 1 ? (
                        t("STATUS_ACTIVE")
                      ) : (
                        <span className="status_inactive">
                          {t("STATUS_INACTIVE")}
                        </span>
                      )}
                    </p>
                    <div className="listCell">
                      <ActionButtonTooltip
                        className="btn btn_type_edit"
                        onClick={() => {
                          setSmsToEdit(m);
                          setShowAddOrEditSmsForm(true);
                        }}
                        text={t('EDIT_SMS')}
                      />
                      <ActionButtonTooltip
                        className="btn btn_type_delete"
                        onClick={() => {
                          setSmsToDelete(m);
                          setOpenModal(true);
                        }}
                        text={t('DELETE_SMS')}
                        tooltipPosition="left"
                      />
                    </div>
                  </li>
                )
            )}
          </ul>
          <ListPagination
            limits={limits}
            total={filteredMessages.length}
            step={limitNumber}
            setLimits={setLimits}
          />
        </article>
      ) : (
        <p className="text">{t("NO_RESULTS_FOUND")}</p>
      )}
      {openModal && (
        <Modal onClose={() => setOpenModal(false)}>
          <DeleteConfirmation
            onClose={() => setOpenModal(false)}
            onDelete={handleDelete}
            text={t('CONF_DELETE_SMS', { title: smsToDelete?.title })}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup
            onClose={() => {
              setOpenModalSuccess(false);
            }}
          />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
    </section>
  );
}

export default SmsPage;
